import { APIProvider } from "@vis.gl/react-google-maps";
import {
  getIsStateRequired,
  getGroupedCountryOptionsByName,
} from "utils/stateRequired";
import { AddressAutocomplete } from "components/AddressComponent/AddressAutocomplete";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik, FormikValues, getIn, useFormikContext } from "formik";
import { useToast } from "hooks/useToast";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateCustomerAddressMutation,
  useGetAllCustomerAddressesQuery,
  useUpdateCustomerAddressMutation,
} from "store/apis/invoices";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { CustomerAddress } from "types/Models/invoice";
import { BackendError } from "types/utils/error";
import { object, string } from "yup";

const API_KEY = process.env.PUBLIC_GOOGLE_MAP_KEY;

export const InvoiceCustomerAddressComponents = ({
  formPrefix = "",
  required = true,
  groupId,
  entityId,
  selectedAddressId,
  onAddressSelect,
  label,
  onClearAddress,
  handleCreateManually,
  fieldPrefix = "",
  srollToSave,
  hideClear,
  customerId,
  addressType,
}: {
  formPrefix?: string;
  required?: boolean;
  groupId: string;
  entityId: string;
  selectedAddressId?: string;
  onAddressSelect: (address: CustomerAddress | null) => void;
  label?: string;
  onClearAddress?: () => void;
  handleCreateManually?: () => void;
  fieldPrefix?: string;
  srollToSave?: boolean;
  hideClear?: boolean;
  customerId: string;
  addressType: "SHIPPING" | "BILLING";
}) => {
  const { data: countries } = useGetCountriesDataQuery();

  const countryOptions = useMemo(
    () => getGroupedCountryOptionsByName(countries),
    [countries]
  );

  const [createManually, setCreateManually] = useState(false);
  const [defaultAddressId, setDefaultAddressId] = useState<string | null>(null);
  const { successToast, alertToast } = useToast();
  const [editAddressId, setEditAddressId] = useState<string | null>(null);
  const [createCustomerAddress, { isLoading: isCreatingCustomerAddress }] =
    useCreateCustomerAddressMutation();
  const [updateCustomerAddress, { isLoading: isUpdatingCustomerAddress }] =
    useUpdateCustomerAddressMutation();

  const isLoading = isCreatingCustomerAddress || isUpdatingCustomerAddress;

  const { data: allAddresses = [], isLoading: isLoadingAllAddresses } =
    useGetAllCustomerAddressesQuery(
      {
        entityId,
        customerId,
        addressType,
      },
      { skip: !entityId || !groupId || !customerId }
    );

  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const streetAddressKey = `${formPrefix}${fieldPrefix}street_address`;
  const cityKey = `${formPrefix}${fieldPrefix}city`;
  const countryKey = `${formPrefix}${fieldPrefix}country`;
  const zipcodeKey = `${formPrefix}${fieldPrefix}zipcode`;
  const streetLine2Key = `${formPrefix}${fieldPrefix}street_address_line_2`;
  const stateKey = `${formPrefix}${fieldPrefix}state`;

  const { values, setFieldValue } = useFormikContext<Record<string, any>>();

  const defaultAddress = allAddresses.find(
    (address) => address.uuid === defaultAddressId
  );

  const updateAddressFields = (address?: CustomerAddress) => {
    setFieldValue(cityKey, address?.city || "");
    setFieldValue(countryKey, address?.country || "");
    setFieldValue(stateKey, address?.state || "");
    setFieldValue(zipcodeKey, address?.zipcode || "");
    setFieldValue(streetAddressKey, address?.street_address || "");
    setFieldValue(streetLine2Key, address?.street_address_line_2 || "");
  };

  const updateLocalAddressFields = (
    setLocalFieldValue: (field: string, value: any) => void,
    address?: CustomerAddress
  ) => {
    setLocalFieldValue(cityKey, address?.city || "");
    setLocalFieldValue(countryKey, address?.country || "");
    setLocalFieldValue(stateKey, address?.state || "");
    setLocalFieldValue(zipcodeKey, address?.zipcode || "");
    setLocalFieldValue(streetAddressKey, address?.street_address || "");
    setLocalFieldValue(streetLine2Key, address?.street_address_line_2 || "");
  };

  const clearAddress = (
    setLocalFieldValue: (field: string, value: any) => void
  ) => {
    updateLocalAddressFields(setLocalFieldValue, defaultAddress);
    updateAddressFields(defaultAddress);
    setFieldValue(`${formPrefix}${fieldPrefix}uuid`, selectedAddressId);
    onClearAddress?.();
    setCreateManually(false);
    setEditAddressId(null);
  };

  const saveAddress = async (localValues: FormikValues) => {
    try {
      let newAddress = null;

      if (!editAddressId) {
        newAddress = await createCustomerAddress({
          entityId,
          customerId,
          payload: {
            address: {
              ...(localValues as CustomerAddress),
              address_type: addressType as "BILLING" | "SHIPPING",
            },
          },
        }).unwrap();
      } else {
        newAddress = await updateCustomerAddress({
          entityId,
          customerId,
          addressId: editAddressId,
          payload: {
            address: {
              ...(localValues as CustomerAddress),
              address_type: addressType as "BILLING" | "SHIPPING",
            },
          },
        }).unwrap();
      }
      onAddressSelect(newAddress);
      updateAddressFields(newAddress);
      successToast({ message: "Address saved successfully" });
      setCreateManually(false);
      setEditAddressId(null);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onCreateManually = () => {
    setCreateManually(true);
    handleCreateManually?.();
    setEditAddressId(null);
  };

  const onEditAddress = (
    addressId: string,
    setLocalFieldValue: (field: string, value: any) => void
  ) => {
    const selectedAddress = allAddresses.find(({ uuid }) => uuid === addressId);
    updateLocalAddressFields(setLocalFieldValue, selectedAddress);
    updateAddressFields(selectedAddress);
    setFieldValue(`${formPrefix}${fieldPrefix}uuid`, addressId);
    onCreateManually();
    setCreateManually(true);
    setEditAddressId(addressId);
  };

  const handleAddressSelect = (address: CustomerAddress | null) => {
    setDefaultAddressId(address?.uuid || null);
    onAddressSelect(address);
  };

  useEffect(() => {
    if (selectedAddressId) {
      setDefaultAddressId(selectedAddressId);
    }
  }, [selectedAddressId]);

  useEffect(() => {
    if (srollToSave && saveButtonRef.current) {
      saveButtonRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [editAddressId, saveButtonRef.current, srollToSave, createManually]);

  return (
    <Formik
      validateOnChange
      validationSchema={object({
        [streetAddressKey]: string().required("Address Line 1 Required"),
        [cityKey]: string().required("City Required"),
        [countryKey]: string().required("Country Required"),
        [stateKey]: string().when(countryKey, {
          is: (country: string) => getIsStateRequired(countries, country),
          then: string().required("State Required"),
          otherwise: string().notRequired(),
        }),
        [zipcodeKey]: string().required("Required"),
      })}
      onSubmit={saveAddress}
      initialValues={{
        [streetAddressKey]: defaultAddress?.street_address || "",
        [streetLine2Key]: defaultAddress?.street_address_line_2 || "",
        [cityKey]: defaultAddress?.city || "",
        [countryKey]: defaultAddress?.country || "",
        [stateKey]: defaultAddress?.state || "",
        [zipcodeKey]: defaultAddress?.zipcode || "",
      }}
      enableReinitialize
      validateOnMount={false}
    >
      {({ submitForm, setFieldValue: setLocalFieldValue, values }) => {
        const selectedCountry = getIn(values, countryKey);
        const selectedState = getIn(values, stateKey);
        const isStateRequired = getIsStateRequired(countries, selectedCountry);

        return (
          <Form>
            <div onClick={(e) => e.stopPropagation()}>
              {!createManually && (
                <APIProvider
                  apiKey={API_KEY || ""}
                  solutionChannel="GMP_devsite_samples_v3_rgmautocomplete"
                >
                  <div className="autocomplete-control">
                    <AddressAutocomplete
                      formPrefix={formPrefix}
                      availableCountriesData={countries || []}
                      required={required}
                      selectedAddressId={selectedAddressId}
                      savedAddresses={allAddresses}
                      getSavedAddressLabel={(address) => address.address_string}
                      onAddressSelect={handleAddressSelect}
                      onCreateManually={onCreateManually}
                      label={label}
                      field_prefix={fieldPrefix}
                      onEditAddress={(addressId: string) =>
                        onEditAddress(addressId, setLocalFieldValue)
                      }
                      hideClear={hideClear}
                    />
                  </div>
                </APIProvider>
              )}
              {createManually && (
                <div>
                  <Label>{label}</Label>
                  <div className="t-shadow-light-30 t-space-y-4 t-border t-border-solid t-border-neutral-0 t-rounded-lg t-p-4 sm:t-p-3">
                    <div className="t-text-subtitle-sm t-text-text-100">
                      {editAddressId ? "Edit Address" : "Add Address"}
                    </div>
                    <div className="t-relative t-w-full">
                      <TextInput
                        block
                        required={required}
                        label="Address Line 1"
                        placeholder="Street address"
                        name={streetAddressKey}
                      />
                    </div>
                    <div>
                      <TextInput
                        block
                        label="Address Line 2"
                        placeholder="Apartment, suite, building, floor etc."
                        name={streetLine2Key}
                      />
                    </div>
                    <div>
                      <Combobox
                        required={required}
                        name={countryKey}
                        withForm
                        label="Country"
                        placeholder="Select Country"
                        components={{
                          GroupHeading: ({ children }) => (
                            <div className="t-text-neutral-40 t-text-body-sm t-font-light t-pl-3 t-pr-3 t-pb-1 t-pt-3">
                              {children}
                            </div>
                          ),
                        }}
                        options={countryOptions}
                        value={
                          selectedCountry
                            ? {
                                label: selectedCountry,
                                value: selectedCountry,
                              }
                            : null
                        }
                      />
                    </div>
                    <div>
                      <Combobox
                        name={stateKey}
                        required={isStateRequired}
                        withForm
                        label="State"
                        placeholder="Select State"
                        options={
                          countries
                            ?.find((c) => c.name === selectedCountry)
                            ?.states?.map((state) => ({
                              value: state.name,
                              label: state.name,
                            }))!
                        }
                        value={
                          selectedState
                            ? { label: selectedState, value: selectedState }
                            : null
                        }
                      />
                    </div>
                    <div className="t-flex t-gap-6 t-w-full sm:t-flex-col sm:t-gap-4">
                      <div className="t-w-full">
                        <TextInput
                          required={required}
                          block
                          label="City"
                          placeholder="Enter City"
                          name={cityKey}
                        />
                      </div>
                      <div className="t-w-full">
                        <TextInput
                          required={required}
                          block
                          placeholder="Enter Zip, postal or pin code"
                          label="Zip, postal or pin code"
                          name={zipcodeKey}
                        />
                      </div>
                    </div>
                    <div className="t-flex t-justify-end t-gap-4">
                      <Button
                        customType="ghost"
                        type="button"
                        onClick={() => clearAddress(setLocalFieldValue)}
                        size="small"
                      >
                        Cancel
                      </Button>
                      <Button
                        customType="primary"
                        type="submit"
                        disabled={isLoading}
                        isLoading={isLoading}
                        size="small"
                        ref={saveButtonRef}
                        onClick={submitForm}
                      >
                        Save address
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
